import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface FuelDataPoint {
  time: string;
  rawValue: number;
  liters: number;
  sensorNumber: number;
  sensorKey: string;
}

export interface FuelDataResponse {
  imei: string;
  data?: FuelDataPoint[];
}

export interface FuelSummaryPoint {
  time: string;
  totalLiters: number;
  sensorCount: number;
}

export interface FuelStats {
  imei: string;
  startTime: string;
  endTime: string;
  totalDataPoints: number;
  minLiters: number;
  maxLiters: number;
  avgLiters: number;
  totalLiters: number;
  minRawValue: number;
  maxRawValue: number;
  avgRawValue: number;
  sensorCounts: Record<string, number>;
  firstDataTime: string;
  lastDataTime: string;
}

export interface ConsumptionStats {
  sensorNumber: number;
  consumption: number;
  refill: number;
  currentLevel: number;
  maxLevel: number;
  minLevel: number;
  dataPoints: number;
}

export interface ConsumptionResponse {
  imei: string;
  startTime: string;
  endTime: string;
  totalDataPoints: number;
  periodHours: number;
  avgConsumptionPerHour: number;
  consumptionStats: {
    sensors: Record<string, ConsumptionStats>;
    totalConsumption: number;
    totalRefill: number;
    netChange: number;
    totalSensors: number;
  };
}

export interface RealtimeData {
  imei: string;
  minutes: number;
  startTime: string;
  endTime: string;
  requestTime: string;
  dataPoints: number;
  fuelData?: FuelDataPoint[];
  latestData?: FuelDataPoint;
  latestBySensor?: Record<string, FuelDataPoint>;
  consumptionStats?: {
    sensors: Record<string, ConsumptionStats>;
    totalConsumption: number;
    totalRefill: number;
    netChange: number;
    totalSensors: number;
  };
}

export interface CalibrationInfo {
  imei: string;
  carId: number;
  carName: string;
  tablesCount: number;
  tables: Array<{
    id: number;
    name: string;
    sensorNumber: number;
    sensorType: string;
    isActive: boolean;
    calibrationPointsCount: number;
    firstPoint: {
      rawData: number;
      liters: number;
    };
    lastPoint: {
      rawData: number;
      liters: number;
    };
  }>;
  sampleDataCount: number;
  sampleData: Record<string, any>;
  availableFields: string[];
}

export interface TimezoneInfo {
  timezoneInfo: string;
  kazakhstanOffsetHours: number;
  currentUtcTime: string;
  currentKazakhstanTime: string;
}

class FuelService {
  private baseUrl = '/api/fuel';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение информации о временных зонах
  async getTimezoneInfo(): Promise<TimezoneInfo> {
    const url = getDataApiUrl(`${this.baseUrl}/timezone-info`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение топливных данных в литрах
  async getFuelData(imei: string, startTime: string, endTime: string): Promise<FuelDataResponse> {
    const url = getDataApiUrl(`${this.baseUrl}/${imei}/liters?startTime=${startTime}&endTime=${endTime}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение суммарных топливных данных
  async getFuelSummary(imei: string, startTime: string, endTime: string): Promise<FuelSummaryPoint[]> {
    const url = getDataApiUrl(`${this.baseUrl}/${imei}/summary?startTime=${startTime}&endTime=${endTime}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение статистики топливных данных
  async getFuelStats(imei: string, startTime: string, endTime: string): Promise<FuelStats> {
    const url = getDataApiUrl(`${this.baseUrl}/${imei}/stats?startTime=${startTime}&endTime=${endTime}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение статистики потребления топлива
  async getFuelConsumption(imei: string, startTime: string, endTime: string): Promise<ConsumptionResponse> {
    const url = getDataApiUrl(`${this.baseUrl}/${imei}/consumption?startTime=${startTime}&endTime=${endTime}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение данных в реальном времени
  async getRealtimeData(imei: string, minutes: number = 5): Promise<RealtimeData> {
    const url = getDataApiUrl(`${this.baseUrl}/${imei}/realtime?minutes=${minutes}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение информации о калибровке
  async getCalibrationInfo(imei: string): Promise<CalibrationInfo> {
    const url = getDataApiUrl(`${this.baseUrl}/${imei}/calibration-info`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение топливных данных по машине
  async getFuelDataByCar(carId: number, startTime: string, endTime: string): Promise<FuelDataResponse> {
    const url = getDataApiUrl(`${this.baseUrl}/car/${carId}/liters?startTime=${startTime}&endTime=${endTime}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }
}

const fuelService = new FuelService();
export default fuelService; 